import globalColors, {
  globalGradientDarkColors,
  globalGradientLightColors,
} from "./GlobalConstants";

export const formatDate = (inputDate) => {
  if (!inputDate) return "";

  try {
    const date = new Date(inputDate);
    if (isNaN(date)) return "";

    return date.toLocaleDateString("en-US", {
      weekday: "short",
      month: "short",
      year: "numeric",
    });
  } catch (error) {
    console.error("Date formatting error:", error);
    return "";
  }
};

// formats both date and time
export const formatDateTime = (inputDate) => {
  if (!inputDate) return "";

  try {
    const date = new Date(inputDate);
    if (isNaN(date)) return "";

    return date.toLocaleString("en-US", {
      weekday: "short",
      month: "short",
      day: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  } catch (error) {
    console.error("DateTime formatting error:", error);
    return "";
  }
};

// Converts snake_case or kebab-case to Title Case
export const formatLabel = (str = "") => {
  return str
    .replace(/[_-]/g, " ") // replace _ or - with space
    .replace(
      /\w\S*/g,
      (txt) => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase(),
    );
};

export const getPriceDarkBgColor = (price) => {
  if (price <= 20) return globalGradientDarkColors.commanGray;
  if (price <= 50) return globalGradientDarkColors.greenUncomman;
  if (price <= 250) return globalGradientDarkColors.blueRare;
  if (price <= 2000) return globalGradientDarkColors.purpleEpic;
  return globalGradientDarkColors.goldLegendary;
};

export const getPriceLightBgColor = (price) => {
  if (price <= 20) return globalGradientLightColors.softGrayGlow;
  if (price <= 50) return globalGradientLightColors.softCyanRadiance;
  if (price <= 250) return globalGradientLightColors.softPeriwinkleGlow;
  if (price <= 2000) return globalGradientLightColors.softLilacRadiance;
  return globalGradientLightColors.softVanillaGlow;
};

export const getBadge = (price) => {
  if (price <= 20) {
    return { label: "COMMON", style: { color: "grey" } };
  } else if (price <= 50) {
    return { label: "UNCOMMON", style: { color: globalColors.flashLoot } };
  } else if (price <= 250) {
    return { label: "RARE", style: { color: "#3A5B8C" } };
  } else if (price <= 2000) {
    return { label: "EPIC", style: { color: globalColors.flexLight } };
  } else {
    return {
      label: "LEGENDARY",
      style: { color: globalColors.vaultGold, fontWeight: "bold" },
    };
  }
};

// Shortens a long text and adds a "Read more" toggle dynamically.
export const shortenTextWithReadMore = (text = "", limit = 100) => {
  if (!text || text.length <= limit) return text;
  const shortText = text.slice(0, limit);
  const remainingText = text.slice(limit);
  const id = `readmore-${Math.random().toString(36).substr(2, 9)}`;
  return `
    <span id="${id}-short">
      ${shortText}... 
      <a href="#" onclick="document.getElementById('${id}-short').style.display='none';document.getElementById('${id}-full').style.display='inline';return false;">
        Read more
      </a>
    </span>
    <span id="${id}-full" style="display:none;">
      ${shortText}${remainingText} 
      <a href="#" onclick="document.getElementById('${id}-full').style.display='none';document.getElementById('${id}-short').style.display='inline';return false;">
        Show less
      </a>
    </span>
  `;
};

// truncate text to a specific length and add ellipsis
export const truncateText = (text = "", limit = 20) => {
  if (!text) return "";
  return text.length > limit ? text.substring(0, limit) + "..." : text;
};

export const createPriceBasedGlow = (ctx, imgX, imgY, glowRadius, price, isDarkMode = true) => {
  const glowGradient = ctx.createRadialGradient(imgX, imgY, 0, imgX, imgY, glowRadius);

  if (isDarkMode) {
    if (price <= 20) {
      glowGradient.addColorStop(0, "rgba(73, 52, 55, 1)");
      glowGradient.addColorStop(0.6, "rgba(53, 52, 55, 0.1)");
      glowGradient.addColorStop(1, "rgba(71, 58, 32, 0)");
    } else if (price <= 50) {
      glowGradient.addColorStop(0, "rgba(10, 61, 68, 1)");
      glowGradient.addColorStop(0.6, "rgba(10, 61, 68, 0.1)");
      glowGradient.addColorStop(1, "rgba(10, 61, 68, 0)");
    } else if (price <= 250) {
      glowGradient.addColorStop(0, "rgba(18, 27, 62, 1)");
      glowGradient.addColorStop(0.6, "rgba(18, 27, 62, 0.1)");
      glowGradient.addColorStop(1, "rgba(18, 27, 62, 0)");
    } else if (price <= 2000) {
      glowGradient.addColorStop(0, "rgba(52, 40, 66, 1)");
      glowGradient.addColorStop(0.6, "rgba(52, 40, 66, 0.1)");
      glowGradient.addColorStop(1, "rgba(52, 40, 66, 0)");
    } else {
      glowGradient.addColorStop(0, "rgba(72, 59, 32, 1)");
      glowGradient.addColorStop(0.6, "rgba(72, 59, 32, 0.1)");
      glowGradient.addColorStop(1, "rgba(72, 59, 32, 0)");
    }
  } else {
    if (price <= 20) {
      // softGrayGlow
      glowGradient.addColorStop(0, "rgba(180, 180, 180, 1)");
      glowGradient.addColorStop(0.6, "rgba(180, 180, 180, 0.2)");
      glowGradient.addColorStop(1, "rgba(180, 180, 180, 0)");
    } else if (price <= 50) {
      // softCyanRadiance
      glowGradient.addColorStop(0, "rgba(100, 240, 248, 1)");
      glowGradient.addColorStop(0.6, "rgba(100, 240, 248, 0.2)");
      glowGradient.addColorStop(1, "rgba(100, 240, 248, 0)");
    } else if (price <= 250) {
      // softPeriwinkleGlow
      glowGradient.addColorStop(0, "rgba(140, 165, 255, 1)");
      glowGradient.addColorStop(0.6, "rgba(140, 165, 255, 0.2)");
      glowGradient.addColorStop(1, "rgba(140, 165, 255, 0)");
    } else if (price <= 2000) {
      // softLilacRadiance
      glowGradient.addColorStop(0, "rgba(193, 145, 255, 1)");
      glowGradient.addColorStop(0.6, "rgba(193, 145, 255, 0.2)");
      glowGradient.addColorStop(1, "rgba(193, 145, 255, 0)");
    } else {
      // softVanillaGlow
      glowGradient.addColorStop(0, "rgba(255, 201, 100, 1)");
      glowGradient.addColorStop(0.6, "rgba(255, 201, 100, 0.2)");
      glowGradient.addColorStop(1, "rgba(255, 201, 100, 0)");
    }
  }

  return glowGradient;
};

// Universal Copy Helper
export const copyToClipboard = async (
  text,
  { onSuccess, onError, successMessage = "Copied to clipboard!" } = {},
) => {
  if (text === undefined || text === null) return false;

  const value = String(text);

  try {
    // Modern clipboard API
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(value);
    } else {
      // Fallback
      const textArea = document.createElement("textarea");
      textArea.value = value;
      textArea.style.position = "fixed";
      textArea.style.left = "-9999px";
      textArea.style.top = "-9999px";

      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();

      document.execCommand("copy");
      document.body.removeChild(textArea);
    }

    onSuccess?.(successMessage);
    return true;
  } catch (err) {
    console.error("Copy failed:", err);
    onError?.("Failed to copy");
    return false;
  }
};
